import { useEffect, useState } from 'react'
import { Alert, Button, Modal, Segmented, type SegmentedOption } from '@indipia/ui-core'
import { todayKst } from '@indipia/shared-features'
import { orderApi, OrderApiError, ORDER_ERROR_NETWORK, ORDER_ERROR_MODE_PAST_TASK } from '../../api/orderApi'
import { ORDER_MESSAGE } from '../../constants/materialOrder.logic'
import type { OrderMode } from '../../constants/orderSettings.logic'
import { TaskDetailCard } from '../TaskDetailCard'

// 간트 공종 상세창 "발주·시공 방식" 카드(SIF-1 4-3) — 회사 기본값(H-5)과 별개로 이 현장·이 공종만 바꾼다.
// 위치: 상세창 맨 위(아래 시공파트너·자재파트너 카드가 이 방식에 따라 바뀌므로). 3칸 선택(별도발주 / 발주없음 / 자재파트너가 시공)을
// 바꾸면 현장 공종 방식 변경 PATCH 를 호출하고, 성공하면 onChanged 로 상세창·간트를 다시 읽게 한다.
// 전환 시 시공파트너 배정 편입/해제·일정확인 대상 편입은 서버가 처리한다(FE 는 refetch 만).

const MSG = ORDER_MESSAGE.settings

/** 고른 방식이 무엇을 뜻하는지 — H-5 표의 방식 설명과 같은 문구 */
const MODE_DESC: Record<OrderMode, string> = {
  SEPARATE: MSG.modeSeparateDesc,
  NONE: MSG.modeNoneDesc,
  VENDOR_CONSTRUCT: MSG.modeVendorConstructDesc,
}

const OPTIONS: SegmentedOption[] = [
  { value: 'SEPARATE', label: MSG.modeSeparate },
  { value: 'NONE', label: MSG.modeNone },
  { value: 'VENDOR_CONSTRUCT', label: MSG.modeVendorConstruct },
]

interface Props {
  taskId: number
  /** 이 task 의 현재 방식(회사 기본값 + 현장 override 합성) */
  value: OrderMode
  /** 이 task 종료일('YYYY-MM-DD'). 서울 기준 오늘보다 이전이면 방식 변경을 막는다 */
  endDate?: string
  /** 방식 변경 성공 시 — 상세창·간트 재조회(배정/빗금/카드 자동 반영) */
  onChanged?: () => void
  /**
   * 일정이 확정된 시공파트너 이름 — 있으면 '자재파트너가 시공'으로 바꾸기 전에 확인창을 띄운다
   * (서버가 그 반장을 해제하고 '배정 취소' 카톡을 보낸다, SIF-1 4-3). 확정 반장이 없으면 null.
   */
  confirmedForemanName?: string | null
}

export function OrderModeCard({ taskId, value, endDate, onChanged, confirmedForemanName }: Props) {
  const [saving, setSaving] = useState(false)
  // '자재파트너가 시공' 전환 확인창 — 확정 반장이 있을 때만
  const [confirmOpen, setConfirmOpen] = useState(false)
  // 저장 중에는 고른 값을 바로 비추고(낙관), 재조회로 value 가 따라오면 자연히 일치한다
  const [pending, setPending] = useState<OrderMode | null>(null)
  const [error, setError] = useState('')
  // 방식 변경 뒤 발주정보가 이미 거래처에 나간 이력이 있으면 안내를 띄운다
  const [resendNotice, setResendNotice] = useState(false)
  const locked = !!endDate && endDate < todayKst()

  // 다른 공종으로 바뀌면 낙관 표시·오류를 초기화한다
  useEffect(() => { setPending(null); setError(''); setResendNotice(false); setConfirmOpen(false) }, [taskId])

  const current = pending ?? value

  const change = async (next: OrderMode) => {
    if (locked || saving || next === current) return
    setSaving(true)
    setPending(next)
    setError('')
    setResendNotice(false)
    try {
      const res = await orderApi.patchTaskOrderMode(taskId, next)
      setResendNotice(res.orderInfoAlreadySent)
      onChanged?.()
    } catch (e) {
      setPending(null)
      if (e instanceof OrderApiError && e.code === ORDER_ERROR_MODE_PAST_TASK) setError(e.serverMessage || MSG.modePastLocked)
      else setError(e instanceof OrderApiError && e.code === ORDER_ERROR_NETWORK ? MSG.network : MSG.unknown)
    } finally {
      setSaving(false)
    }
  }

  // 카드명 · 3칸 선택(한눈에 비교) · 고른 방식이 뭘 뜻하는지 한 줄 + '이 현장에만 적용' 안내
  // 저장 중·지난 공종은 fieldset disabled 로 세 버튼을 한 번에 막는다
  return (
    <TaskDetailCard title={MSG.modeColHead}>
      <fieldset
        disabled={saving || locked}
        aria-label={MSG.modeColHead}
        className="m-0 min-w-0 border-0 p-0"
        onMouseDown={e => e.stopPropagation()}
      >
        <Segmented
          size="sm"
          options={OPTIONS}
          value={current}
          onChange={val => {
            const next = val as OrderMode
            // 확정 반장이 있는 공종을 '자재파트너가 시공'으로 → 배정 취소 카톡이 나가므로 먼저 확인받는다
            if (next === 'VENDOR_CONSTRUCT' && next !== current && confirmedForemanName) setConfirmOpen(true)
            else void change(next)
          }}
          className="w-full [&>button]:flex-1"
        />
      </fieldset>
      <div className="mt-1" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>
        {MODE_DESC[current]}
        <span style={{ color: 'var(--shell-hint)' }}>{` · ${MSG.siteOverrideHint}`}</span>
      </div>
      {locked && !error && <div className="mt-1" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>{MSG.modePastLocked}</div>}
      {resendNotice && <Alert kind="info" className="mt-1">{MSG.orderInfoResend}</Alert>}
      {error && <div role="alert" className="mt-1" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--status-danger-600)' }}>{error}</div>}

      {/* '자재파트너가 시공' 전환 확인 — 같은 상세창의 확인 모달(PartnerCard 일정 겹침)과 같은 구성 */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="확정 반장을 해제할까요?"
        footerAlign="stretch"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>취소</Button>
            <Button variant="primary" onClick={() => { setConfirmOpen(false); void change('VENDOR_CONSTRUCT') }}>변경하기</Button>
          </>
        }
      >
        {/* 문구는 확정 반장 변경 확인(ForemanChangeNotice)과 같은 표현 */}
        <div style={{ fontSize: 'var(--type-body-sm-size)', lineHeight: 1.6, color: 'var(--shell-on-surface)' }}>
          이미 일정을 확정한 반장님이에요. &lsquo;자재파트너가 시공&rsquo;으로 바꾸면 <b>{confirmedForemanName}</b> 반장님께 &lsquo;배정 취소&rsquo; 카톡이 가요.
        </div>
      </Modal>
    </TaskDetailCard>
  )
}
